import { useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import type {
  CustomerFormValues,
  TravelBookingFormValues,
} from "../lib/schemas/travel-booking";
import { CheckCircle, CirclePlus, Edit, Trash } from "lucide-react";
import { CustomerModal } from "./customer-modal";
import { ProductModal } from "./product-modal";
import { useFieldArray } from "react-hook-form";
import { FormField } from "../ui-form/form-field";
import { useQuery } from "react-query";
import { CustomerResponse, getCustomers } from "../apis";
import { useDebounce } from "use-debounce";
import {
  step2,
  products as productsConfig,
} from "../lib/FormConfigs";
import { renderField } from "../lib/FormConfigs/Renderer";
import { FieldConfig } from "../lib/FormConfigs/config";
import { LinkedAutoComplete } from "../ui-form/linked-controls";
import { Input } from "@/components/ui/input";
import { set } from "lodash";

interface CustomerStepProps {
  form: UseFormReturn<TravelBookingFormValues>;
  config?: FieldConfig[];
  isDataFormInvoice?: boolean;
}

export interface CustomerOption {
  value: string;
  label: string;
  customer: {
    phone_number: string;
    full_name: string;
    email: string;
    pan_no: string;
    id: string;
    kyc_status?: string;
  };
}

export function CustomerStep({ form, config, isDataFormInvoice }: CustomerStepProps) {
  const { fields, append, update, remove } = useFieldArray({
    control: form.control,
    name: "customers",
  });

  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [currentCustomerIndex, setCurrentCustomerIndex] = useState<number | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [quantityEditMode, setQuantityEditMode] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [currentValueEditing, setCurrentValueEditing] = useState<number>(0);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 800);
  const new_order_type = new URLSearchParams(window.location.search).get("type") || "";
  const customerStep = step2[new_order_type as keyof typeof step2];
  const productConfig = productsConfig[new_order_type as keyof typeof productsConfig];

  const { data: customerSuggestions, isLoading: isLoadingCustomers } = useQuery<CustomerResponse[]>({
    queryKey: ["customers", debouncedSearchTerm],
    queryFn: () => {
      if (!debouncedSearchTerm || debouncedSearchTerm.trim() === "") {
        return [];
      }
      return getCustomers(searchTerm);
    },
    enabled: searchTerm.length >= 4 && debouncedSearchTerm.trim() !== "",
    staleTime: 1000,
  });

  const handleCustomerSelection = (selected: CustomerOption | null) => {
    if (selected) {
      form.setValue("customers.0.mobileNumber", selected.customer.phone_number);
      form.setValue("customers.0.fullName", selected.customer.full_name);
      form.setValue("customers.0.email", selected.customer.email);
      form.setValue("customers.0.pan", selected.customer.pan_no);
      if (selected.customer.kyc_status) {
        form.setValue("customers.0.kyc_status" as any, selected.customer.kyc_status);
        console.log('KYC Status stored:', selected.customer.kyc_status);
      }
    }
  };

  const handleAddCustomer = (customer: CustomerFormValues) => {
    if (editMode && currentCustomerIndex !== null) {
      update(currentCustomerIndex, customer);
    } else {
      append(customer);
    }
    setIsCustomerModalOpen(false);
    setEditMode(false);
    setCurrentCustomerIndex(null);
  };

  const handleEditCustomer = (index: number) => {
    setCurrentCustomerIndex(index);
    setEditMode(true);
    setIsCustomerModalOpen(true);
  };

  const handleAddProduct = (index: number) => {
    setCurrentCustomerIndex(index);
    setIsProductModalOpen(true);
  };

  const handleRemoveProduct = (customerIndex: number, productIndex: number) => {
    const customer = form.getValues(`customers.${customerIndex}`);
    if (customer.products.length === 1) {
      remove(customerIndex);
      return;
    }
    const updatedProducts = customer.products.filter((_, i) => i !== productIndex);
    update(customerIndex, { ...customer, products: updatedProducts });
  };

  const handleQuantityEdit = (customerIndex: number, productIndex: number | null) => {
    setCurrentCustomerIndex(customerIndex);
    setQuantityEditMode(productIndex);
  };

  // Get valid customers with products
  const validCustomers = fields
    .map((field, index) => {
      const customer = form.getValues(`customers.${index}`) as CustomerFormValues;
      return { index, customer, field };
    })
    .filter((item) =>
      item.customer?.fullName &&
      item.customer?.products?.length > 0 &&
      item.customer?.customerId
    );

  // Pagination logic
  const totalCustomers = validCustomers.length;
  const totalPages = Math.ceil(totalCustomers / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedCustomers = validCustomers.slice(startIndex, endIndex);

  const products = form.watch("customers.0.products") || [];
  const customers = form.getValues("customers")

  useEffect(() => {
    const currentCustomer = form.getValues("customers")?.find((_, index) => index === currentCustomerIndex);
    form.setValue("order_type", currentCustomer?.products?.[0]?.productType?.type as "buy" | "sell")
    const customersInForm = form.getValues("customers");
    if (
      fields.length === 0 &&
      Array.isArray(customersInForm) &&
      customersInForm.length > 0
    ) {
      customersInForm
        .filter((customer) => customer && Object.keys(customer).length > 0)
        .forEach((customer: CustomerFormValues) => {
          append(customer);
        });

    }
  }, [products, currentCustomerIndex]);
  // if(customers.every(customer => !customer?.customerId)) {
  //   return <div>
  //     {products?.length}
  //     {customers?.length}
  //   </div>
  // }

  // If no products in first customer, show the input form
  if (!products?.length || customers.every(customer => !customer?.customerId)) {
    return (
      <div className="space-y-4 max-w-sm">
        <div className="grid grid-cols-[auto_1fr] gap-x-[18.5px]">
          {config?.map((item, index) => {
            const isCompleted = form.watch(item.name as any);
            const isLast = index === config.length - 1;
            return (
              <>
                <div
                  key={`indicator-${item.name}`}
                  className="flex flex-col items-center justify-start"
                >
                  <div
                    className={`
                    w-[9px] h-[9px] rounded-full border mt-1.5 flex-shrink-0
                    transition-colors duration-300
                    ${isCompleted ? "bg-blue-700 border-blue-700" : "bg-white-A700 border-blue-700"}
                  `}
                  />
                  {!isLast && (
                    <div
                      className={`w-0.5 flex-1 border-l-2 border-dotted min-h-[1.5rem] ${isCompleted ? "border-blue-700 " : "border-gray-300 "
                        }`}
                    />
                  )}
                </div>
                {item.name === "customers.0.mobileNumber" ? (
                  <div key={`field-${item.name}`} className="pb-6">
                    <FormField
                      key={index}
                      isOptionalField
                      name="customers.0.mobileNumber"
                      label="What is the mobile number of customer?"
                      render={({ field }) => (
                        <LinkedAutoComplete
                          value={{
                            value: form.getValues("customers.0.mobileNumber"),
                            label: form.getValues("customers.0.mobileNumber"),
                          }}
                          onValueChange={sel => {
                            const customer = customerSuggestions?.find(
                              (c) => c.id?.toString() === sel?.value
                            );
                            if (customer?.id) {
                              field.onChange(customer?.phone_number || "");
                              form.setValue("customers.0.customerId", customer.id.toString());
                              handleCustomerSelection({
                                value: customer.phone_number,
                                label: `${customer.full_name} (${customer.phone_number})`,
                                customer: {
                                  phone_number: customer.phone_number,
                                  full_name: customer.full_name,
                                  email: customer.email,
                                  pan_no: customer.pan_no,
                                  id: customer.id.toString(),
                                  kyc_status: customer.kyc_status,
                                },
                              });
                            } else {
                              form.setValue("customers.0.customerId", "");
                              field.onChange(sel.label || "");
                            }
                            setSearchTerm(field.value || "");
                          }}
                          options={
                            customerSuggestions?.map((customer) => ({
                              value: customer?.id.toString(),
                              label: `${customer?.full_name} (${customer?.phone_number})`,
                            })) || []
                          }
                          inputValue={searchTerm}
                          onInputChange={(value) => {
                            if (value.length < 10 && form.getValues("customers.0.customerId")) {
                              form.setValue("customers.0.customerId", "");
                              handleCustomerSelection({
                                value: form.getValues("customers.0.mobileNumber"),
                                label: form.getValues("customers.0.mobileNumber"),
                                customer: {
                                  phone_number: '',
                                  full_name: '',
                                  email: '',
                                  pan_no: '',
                                  id: '',
                                },
                              });
                            }
                            setSearchTerm(value);
                            field.onChange(value);
                          }}
                          emptyMessage="No customers found"
                          isLoading={isLoadingCustomers}
                          placeholder="Search and select customer"
                        />
                      )}
                    />
                  </div>
                ) : (
                  <div key={`field-${item.name}`} className="pb-6">
                    <FormField
                      key={index}
                      isOptionalField={item.required}
                      name={item.name}
                      label={item.label}
                      render={({ field }) => renderField(item, { ...field, disabled: !!form.getValues("customers.0.customerId") })}
                    />
                  </div>
                )}
              </>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Add Customer button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Customers</h3>
        {/* <Button
          onClick={() => {
            setIsCustomerModalOpen(true);
            setEditMode(false);
            setCurrentCustomerIndex(null);
          }}
          className="flex items-center gap-2 text-blue-700 border border-blue-700 bg-white hover:bg-blue-50"
          variant="outline"
        >
          <CirclePlus className="h-4 w-4" />
          Add customer
        </Button> */}
      </div>

      {/* Customer Tables */}
      <div className="space-y-6">
        {paginatedCustomers.length === 0 ? (
          <div className="text-center bg-gray-100 py-36 border rounded-md">
            <p className="text-[#555555] flex items-center gap-2 justify-center text-[#555555]">
              <CirclePlus className="h-4 w-4" />
              Add customers to view here
            </p>
          </div>
        ) : (
          paginatedCustomers.map(({ customer, index: originalIndex }) => (
            <div key={`customer-${originalIndex}`} className="border rounded-lg overflow-hidden">
              {/* Customer Header */}
              <div className="bg-white px-6 py-4 border-b flex items-center justify-between">
                <h4 className="font-medium text-lg">{customer.fullName}</h4>
                {
                  !form.getValues().invoice?.invoiceFile?.name &&
                  <Button
                    onClick={() => handleAddProduct(originalIndex)}
                    className="flex items-center gap-2 text-blue-700 bg-transparent hover:bg-blue-50 p-2"
                    variant="ghost"
                    size="sm"
                  >
                    <CirclePlus className="h-4 w-4" />
                    Add product
                  </Button>
                }
              </div>

              {/* Products Table */}
              <div className="bg-white">
                {/* Table Header */}
                <div className="bg-[#F5F6FA] grid grid-cols-12 gap-4 px-6 py-3 text-[#222] font-semibold text-sm">
                  <div className="col-span-3 flex items-center gap-2">
                    Product
                  </div>
                  <div className="col-span-3">FCY Amount</div>
                  <div className="col-span-3">Customer Rate</div>
                  {
                    isDataFormInvoice ? null :
                      <div className="col-span-3 text-center">Actions</div>
                  }
                </div>

                {/* Product Rows */}
                {(() => {
                  // Group products by type
                  const groupedProducts: { [key: string]: typeof customer.products } = {};

                  customer.products?.forEach((product) => {
                    const productTypeValue = product?.productType?.value;
                    const isCard = productTypeValue === "16" || productTypeValue === "36";
                    const isCurrencyNote = productTypeValue === "9";

                    if (isCard) {
                      // Group all cards together
                      const cardKey = "cards";
                      if (!groupedProducts[cardKey]) {
                        groupedProducts[cardKey] = [];
                      }
                      groupedProducts[cardKey].push(product);
                    } else if (isCurrencyNote) {
                      // Group all currency notes together
                      const currencyKey = "currency_notes";
                      if (!groupedProducts[currencyKey]) {
                        groupedProducts[currencyKey] = [];
                      }
                      groupedProducts[currencyKey].push(product);
                    } else {
                      // Other products remain separate
                      groupedProducts[`product_${product?.local_id}`] = [product];
                    }
                  });

                  return Object.entries(groupedProducts).map(([groupKey, productsInGroup], groupIndex) => {
                    const firstProduct = productsInGroup?.[0];
                    const productTypeLabel = firstProduct?.productType?.label || 'Forex';
                    const productTypeValue = firstProduct?.productType?.value;

                    const isCard = productTypeValue === "16" || productTypeValue === "36";
                    const isCurrencyNote = productTypeValue === "9";
                    const isGrouped = isCard || isCurrencyNote;

                    // Collect all currencies from all products in this group
                    const allCurrencies = productsInGroup?.flatMap((product, productIndexInGroup) =>
                      product.currencies.map((currency, currencyIndex) => ({
                        currency,
                        productIndex: customer?.products?.findIndex(p => p.local_id === product.local_id),
                        currencyIndex,
                        productIndexInGroup
                      }))
                    );

                    return (
                      <div
                        key={`group-${groupKey}`}
                        className="border-b last:border-b-0"
                      >
                        {allCurrencies?.map(({ currency: currencyItem, productIndex, currencyIndex, productIndexInGroup }, idx) => {
                          const fcyAmount = Number(currencyItem?.amount) || 0;
                          const rate = Number(currencyItem?.rate?.toString()) || 1;
                          const inrAmount = (fcyAmount * rate).toFixed(2);
                          const currency = currencyItem?.currency?.label || 'USD';

                          const isEditingCurrent = quantityEditMode === productIndex;

                          const isFirstCurrency = idx === 0;

                          // Show product name only on first currency when grouped
                          const shouldShowProductName = isGrouped ? isFirstCurrency : true;

                          return (
                            <div
                              key={`currency-${idx}`}
                              className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50"
                            >
                              {/* Product Name */}
                              <div className="col-span-3">
                                {shouldShowProductName && (
                                  <div>
                                    <div className="font-medium text-[#222]">{productTypeLabel}</div>
                                  </div>
                                )}
                              </div>

                              {/* FCY Amount */}
                              {isEditingCurrent ? (
                                <div className="col-span-3 flex items-start gap-2">
                                  <div className="">
                                    <Input
                                      type="number"
                                      value={currentValueEditing}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value);
                                        setCurrentValueEditing(val);
                                      }}
                                    />
                                    <small className="text-[#555555]">Prev: {fcyAmount}</small>
                                  </div>
                                  <Button
                                    disabled={!currentValueEditing}
                                    onClick={() => {
                                      const updatedProducts = [...(customer.products || [])];
                                      if (updatedProducts[productIndex]?.currencies[currencyIndex]) {
                                        updatedProducts[productIndex].currencies[currencyIndex].amount = currentValueEditing;
                                        update(originalIndex, { ...customer, products: updatedProducts });
                                      }
                                      setQuantityEditMode(null)
                                    }}
                                  >
                                    <CheckCircle
                                      className="text-green-500 cursor-pointer"
                                    />
                                  </Button>
                                </div>
                              ) : (
                                <div className="col-span-3 text-[#222] font-medium">
                                  {currency} {fcyAmount}
                                </div>
                              )}

                              {/* Customer Rate */}
                              <div className="col-span-3 text-[#222] font-medium">
                                INR {rate.toFixed(2)}
                              </div>

                              {/* Actions */}
                              {
                                isDataFormInvoice ? null :
                                  <div className="col-span-3 flex items-center justify-center gap-2">
                                    <Button
                                      onClick={() => {
                                        setQuantityEditMode(productIndex as number)
                                        setCurrentValueEditing(fcyAmount);
                                      }}
                                      aria-label="Edit the amount"
                                      className="text-blue-600 hover:bg-blue-50 p-1 cursor-pointer"
                                      variant="ghost"
                                      size="sm"
                                    >
                                      <Edit aria-hidden className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      onClick={() => {
                                        handleRemoveProduct(originalIndex, (productIndex as number));
                                      }}
                                      aria-label="Remove this product"
                                      className="text-red-600 hover:bg-red-50 p-1 cursor-pointer"
                                      variant="ghost"
                                      size="sm"
                                    >
                                      <Trash aria-hidden className="h-4 w-4" />
                                    </Button>
                                  </div>
                              }
                            </div>
                          );
                        })}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {/* {totalCustomers > 0 && (
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#555555]">
              Page {currentPage}-{Math.min(currentPage * rowsPerPage, totalCustomers)} of {totalCustomers}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#555555]">Rows per page</span>
              <Select
                value={{ value: rowsPerPage.toString(), label: rowsPerPage.toString() }}
                onChange={(option) => {
                  if (option) {
                    setRowsPerPage(parseInt(option.value));
                    setCurrentPage(1);
                  }
                }}
                options={[
                  { value: '5', label: '5' },
                  { value: '8', label: '8' },
                  { value: '10', label: '10' },
                  { value: '20', label: '20' },
                ]}
                className="w-20"
                isSearchable={false}
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
            >
              Back
            </Button>
            <Button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages}
              variant="outline"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )} */}
      {
        form.getValues().invoice?.invoiceFile?.name &&
        <p className="text-sm text-[#555555]"><span className="text-blue-700 pr-2">&#x24D8;</span>Products can not be edited as invoice has been generated.</p>
      }

      {/* Modals */}
      {isCustomerModalOpen && (
        <CustomerModal
          productConfig={productConfig}
          config={customerStep}
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSubmit={handleAddCustomer}
          initialData={
            editMode && currentCustomerIndex !== null
              ? form.getValues(`customers.${currentCustomerIndex}`)
              : undefined
          }
        />
      )}

      {isProductModalOpen && (
        <ProductModal
          config={productConfig}
          initialData={
            currentCustomerIndex !== null
              ? form.getValues(`customers.${currentCustomerIndex}`).products as CustomerFormValues["products"]
              : undefined
          }
          isOpen={isProductModalOpen}
          order_type={form.getValues("order_type")}
          onClose={() => setIsProductModalOpen(false)}
          onSubmit={(product) => {
            if (currentCustomerIndex !== null) {
              const customer = form.getValues(`customers.${currentCustomerIndex}`) as CustomerFormValues;
              const updatedCustomer = {
                ...customer,
                products: [...(customer?.products || []), product],
              };
              update(currentCustomerIndex, updatedCustomer);
            }
            setIsProductModalOpen(false);
          }}
        />
      )}
    </div>
  );
}